import React, { useState } from 'react';
import { useLayoutContext } from 'contexts/LayoutContext';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import { CalendarDay, pluralize } from 'further-ui/utils';
import api from 'lib/trpcClient';
import TransactionAllocator from 'components/TransactionAllocator';
import PageLoader from 'components/PageLoader';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import CurrencyRate from 'components/CurrencyRate';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { RolePermission } from 'further-types/admin-user-role';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { ActionRefLabels } from 'further-ui/labels';
import { Alert, Button, Typography } from '@mui/material';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  createLpPeriodEndReviewDetailRoute,
  LP_PERIOD_END_REVIEW,
} from 'adminConstants/routes';
import ProcessingPage from 'components/new/ProcessingPage';
import Table from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { styled } from '@mui/material/styles';
import { Row } from 'components/Layout';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column } from 'further-ui/components/Layout';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  onExit: () => void;
};

const ApproveTransactionRates: React.FC<Props> = ({ onExit }) => {
  const { vehicle } = useLayoutContext();
  const { search } = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const { periodId } = useParams<{ periodId?: string }>();
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam
    ? new CalendarDay(periodEndParam)
    : new CalendarDay(new Date());

  const [overrideRates, setOverrideRates] = useState<
    Record<string, CurrencyRateDetails>
  >({});
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [isProcessingUnapproval, setIsProcessingUnapproval] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();
  const approveDialog = useDisclosure();
  const unapproveDialog = useDisclosure();

  const [canEditAccountingPeriod] = useHasPermissions([
    RolePermission.LpEditAccountingPeriod,
  ]);

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data: period } = api.accountingPeriod.getPeriodById.useQuery(
    { vehicleId: vehicle?._id ?? '', id: periodId ?? '' },
    { enabled: !!vehicle && !!periodId },
  );

  const isConfirmed = !!period?.checklist.transactionRates.confirmed;

  const { data: actions, isLoading } =
    api.transactionFx.listActionsForRateApproval.useQuery(
      periodStart && periodEnd
        ? {
            vehicleId: vehicle?._id ?? '',
            periodStart,
            periodEnd,
          }
        : {
            vehicleId: vehicle?._id ?? '',
            date: periodEnd,
          },
      { enabled: !!vehicle && !isConfirmed },
    );

  const { data: approvedActions, isLoading: approvedIsLoading } =
    api.transactionFx.listApprovedTransactionRates.useQuery(
      { vehicleId: vehicle?._id ?? '', periodId: periodId ?? '' },
      { enabled: !!vehicle && !!periodId && isConfirmed },
    );

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: approveFxRates } =
    api.transactionFx.approveFxRates.useMutation(
      mutationOpts({
        successMessage: 'Transaction rates approved successfully.',
        refetchType: 'active',
        onSuccess: () =>
          history.push(
            periodId
              ? createLpPeriodEndReviewDetailRoute(periodId)
              : LP_PERIOD_END_REVIEW,
          ),
      }),
    );

  const { mutateAsync: unconfirmChecklistItem } =
    api.accountingPeriod.unconfirmChecklistItem.useMutation(
      mutationOpts({
        successMessage: 'Transaction rates unapproved successfully.',
        refetchType: 'active',
        onSuccess: () =>
          history.push(
            periodId
              ? createLpPeriodEndReviewDetailRoute(periodId)
              : LP_PERIOD_END_REVIEW,
          ),
      }),
    );

  const handleExit = () => {
    onExit();
  };

  const handleApprove = async () => {
    if (!actions?.length || !vehicle?._id) return;
    setIsProcessingApproval(true);
    try {
      await approveFxRates({
        vehicleId: vehicle._id,
        actions: actions.map((action) => ({
          id: action.id,
          currencyRate: overrideRates[action.id] ?? action.currencyRate ?? null,
        })),
        periodId: periodId ?? '',
      });
    } catch {
      setIsProcessingApproval(false);
    }
  };

  const handleUnapprove = async () => {
    if (!vehicle?._id || !periodId) return;
    unapproveDialog.onClose();
    setIsProcessingUnapproval(true);
    try {
      await unconfirmChecklistItem({
        vehicleId: vehicle._id,
        id: periodId,
        item: 'transactionRates',
      });
    } catch {
      setIsProcessingUnapproval(false);
    }
  };

  const columns = [
    {
      key: 'action',
      label: '',
      render: (row) => (
        <Column spacing="xs">
          <Row centered spacing="sm">
            <ColorIndicator colorKey={{ actionRef: row.actionRef }} />
            <Typography variant="subtitle2">
              {ActionRefLabels[row.actionRef]} – {row.counterpartyLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.ledgerDate.toReadable()}
            </Typography>
          </Row>
        </Column>
      ),
      disableSorting: true,
    },
  ];

  if (!vehicle || transactionTypesLoading) {
    return <PageLoader />;
  }

  return (
    <FullPageDrawer
      open
      size="lg"
      onClose={handleExit}
      innerScroll
      title={
        isConfirmed
          ? 'Automated Transactions: Approved transaction rates'
          : 'Automated Transactions: Approve transaction rates'
      }
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {isConfirmed
            ? canEditAccountingPeriod && (
                <Button
                  size="large"
                  variant="outlined"
                  color="error"
                  onClick={unapproveDialog.onOpen}
                  disabled={isProcessingUnapproval}
                >
                  Unapprove and revert rates
                </Button>
              )
            : canEditAccountingPeriod &&
              !!actions?.length && (
                <Button
                  size="large"
                  variant="contained"
                  onClick={
                    hasEqualisationInDraftState
                      ? confirmDialog.onOpen
                      : approveDialog.onOpen
                  }
                  disabled={isProcessingApproval}
                >
                  Approve and mark as reviewed
                </Button>
              )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {isProcessingApproval || isProcessingUnapproval ? (
          <ProcessingPage
            title={
              isProcessingUnapproval
                ? 'Unapproving transaction rates'
                : 'Approving transaction rates'
            }
            subtitle="This will only take a moment."
          />
        ) : isConfirmed ? (
          <>
            <Typography variant="h5">Approved transactions</Typography>
            <Typography variant="body2">
              These transaction rates have been approved for the period.
              Unapprove to revert the rates and review again.
            </Typography>
            {!approvedIsLoading && !approvedActions?.length ? (
              <Alert severity="info-light">
                No approved transactions found.
              </Alert>
            ) : (
              <Table
                columns={columns}
                rows={approvedActions ?? []}
                isLoading={approvedIsLoading}
                rowsTotalCount={approvedActions?.length ?? 0}
                hideHeader
                isExpandableRow={() => true}
                isExpandedRow={(row) => expandedRowId === row.id}
                onRowExpand={(row) =>
                  setExpandedRowId((prev) => (prev === row.id ? null : row.id))
                }
                getExpandedContent={(row) => (
                  <ExpandedRowContainer>
                    <Row fullWidth>
                      <TransactionAllocator
                        transactions={row.transactions}
                        transactionCurrency={row.currencyCode}
                        vehicle={vehicle}
                        fxRate={row.currencyRate ?? null}
                        transactionTypeOptions={transactionTypeOptions}
                        readOnly
                        forceLcyCalculation
                        darkBackground
                      />
                    </Row>
                  </ExpandedRowContainer>
                )}
              />
            )}
          </>
        ) : (
          <>
            <Typography variant="h5">Review transactions</Typography>
            <Typography variant="body2">
              Review and approve outstanding transaction rates from{' '}
              {periodStart?.toReadable()} to {periodEnd?.toReadable()}.
            </Typography>
            <BankReconciliationWarningBox
              date={periodEnd.toUTCDate()}
              warningText="There are pending transactions awaiting bank reconciliation. Click below to process these before reviewing accruals, as reconciled transactions may change outstanding accruals."
            />
            {!isLoading && !actions?.length ? (
              <Alert severity="info-light">
                No transactions found for selected date.
              </Alert>
            ) : (
              <Table
                columns={columns}
                rows={actions ?? []}
                isLoading={isLoading}
                rowsTotalCount={actions?.length ?? 0}
                hideHeader
                isExpandableRow={() => true}
                isExpandedRow={(row) => expandedRowId === row.id}
                onRowExpand={(row) =>
                  setExpandedRowId((prev) => (prev === row.id ? null : row.id))
                }
                getExpandedContent={(row) => (
                  <ExpandedRowContainer>
                    <Row fullWidth>
                      <TransactionAllocator
                        transactions={row.transactions}
                        transactionCurrency={row.currencyCode}
                        vehicle={vehicle}
                        fxRate={
                          overrideRates[row.id] ?? row.currencyRate ?? null
                        }
                        transactionTypeOptions={transactionTypeOptions}
                        readOnly
                        forceLcyCalculation
                        darkBackground
                        currencyRateComponent={
                          <CurrencyRate
                            currency={row.currencyCode}
                            date={row.ledgerDate}
                            vehicle={vehicle}
                            fxRate={
                              overrideRates[row.id] ?? row.currencyRate ?? null
                            }
                            isFetchingRate={false}
                            onRateChange={(rate) => {
                              setOverrideRates((prev) => ({
                                ...prev,
                                [row.id]: rate,
                              }));
                            }}
                          />
                        }
                      />
                    </Row>
                  </ExpandedRowContainer>
                )}
              />
            )}
          </>
        )}
      </Column>
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={handleApprove}
          loading={isProcessingApproval}
        />
      )}
      <ConfirmDialog
        open={approveDialog.isOpen}
        onClose={approveDialog.onClose}
        title="Approve transaction rates?"
        content={`This will set the FX rates of ${
          actions?.length ?? 0
        } ${pluralize(
          'transaction set',
          actions?.length ?? 0,
        )} and mark the section as approved.`}
        loading={isProcessingApproval}
        actions={{
          confirm: {
            label: 'Approve and mark as reviewed',
            onClick: () => {
              approveDialog.onClose();
              void handleApprove();
            },
          },
        }}
      />
      <ConfirmDialog
        open={unapproveDialog.isOpen}
        onClose={unapproveDialog.onClose}
        title="Unapprove transaction rates"
        content="This will revert the approved FX rates on these transactions and re-open the section for review. Are you sure?"
        loading={isProcessingUnapproval}
        actions={{
          confirm: {
            label: 'Unapprove',
            color: 'error',
            onClick: handleUnapprove,
          },
        }}
      />
    </FullPageDrawer>
  );
};

export default ApproveTransactionRates;
